import React, { useState, useEffect, useMemo } from 'react';
import { UserCheck, Save, RefreshCcw, Wallet, PiggyBank } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import localApiAdapter from '../../core/services/localApiAdapter';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import QawaemStatsCard from '../../products/saudi/components/QawaemStatsCard';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { getBrandPhonePlaceholder, normalizeBrandPhone } from '../../shared/utils/brandCountry';
import { daysUntilExpiry } from '../../shared/utils/employeePortalService';
import { usePortalEmployeeContext } from './PortalEmployeeContext';
import {
  ALL_HR_DATA_REFRESH_EVENTS,
  dispatchHrEmployeesRefresh,
  useHrApiRefreshListener,
} from '../ressources-humaines/hrRefreshEvents';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import {
  calculatePortalBenefits,
  calculatePortalBonus,
  calculatePortalEosbAccrual,
  calculatePortalEosbBalance,
  formatPortalDate,
  formatPortalMoney,
  getEmployeeNationalityType,
  getPortalContractLabel,
  getPortalGosiStatusKey,
  getPortalNitaqatLabel,
  getPortalYearsOfService,
  normalizePortalProfile,
  SAUDI_ANNUAL_LEAVE_DAYS,
  SAUDI_CITIES,
  SAUDI_REGIONS,
  SAUDI_SICK_LEAVE_DAYS,
  validatePortalPhone,
  EmployeePortalScopeAlert,
  EmployeePortalUnlinkedState,
} from './employeePortalShared';

const NS = 'profilePage';

const EMPTY = {
  nom: '', prenom: '', email: '', telephone: '', adresse: '', ville: '',
  code_postal: '', region: '', fonction: '', affectation: '', situation_familiale: '', enfants: '', notes: '',
};

export default function EmployeePortalProfilePage() {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.employee_portal.pages.${NS}.${key}`, opts);
  const pc = (key, opts) => t(`modules.employee_portal.pages.common.${key}`, opts);
  const qawaem = isSaudiBrand();
  const { email, name, employeeId, resolveError, profileResolved, refreshEmployeeScope } = usePortalEmployeeContext();
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: p('title') });

  const saudiProfile = useMemo(
    () => (profile ? normalizePortalProfile(profile) : null),
    [profile],
  );

  const isSaudi = saudiProfile ? getEmployeeNationalityType(saudiProfile) === 'saudi' : true;

  const familyOpts = [
    { value: 'celibataire', label: p('family_single') },
    { value: 'marie', label: p('family_married') },
    { value: 'divorce', label: p('family_divorced') },
    { value: 'veuf', label: p('family_widowed') },
  ];

  const loadProfile = async () => {
    if (!employeeId && !email) {
      setProfile(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = {};
      if (employeeId) params.employee_id = employeeId;
      else if (email) params.email = email;
      const res = await localApiAdapter.get('/api/employee-portal/profile', { params });
      const row = res && typeof res === 'object' && !Array.isArray(res) && res.id ? res : null;
      setProfile(row);
      if (!row) {
        setFormData({ ...EMPTY, email: email || '' });
        return;
      }
      const normalized = normalizePortalProfile(row);
      setFormData({
        nom: row.nom ?? '',
        prenom: row.prenom ?? '',
        email: row.email ?? email ?? '',
        telephone: normalized.telephone ?? row.telephone ?? '',
        adresse: row.adresse ?? '',
        ville: row.ville ?? '',
        code_postal: row.code_postal ?? '',
        region: normalized.region ?? '',
        fonction: row.fonction ?? '',
        affectation: row.affectation ?? '',
        situation_familiale: row.situation_familiale ?? '',
        enfants: row.enfants != null ? String(row.enfants) : '',
        notes: row.notes ?? '',
      });
    } catch (e) {
      console.error('[EmployeePortal] Profile load failed:', e?.message || e);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfile(); }, [email, employeeId]);

  useHrApiRefreshListener(ALL_HR_DATA_REFRESH_EVENTS, loadProfile);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!profile?.id) return;
    if (!validatePortalPhone(formData.telephone)) {
      notifyError(showNotify, p('phone_invalid'));
      return;
    }
    try {
      setSaving(true);
      await localApiAdapter.put(`/api/employee-portal/profile/${profile.id}`, {
        ...formData,
        telephone: normalizeBrandPhone(formData.telephone),
        enfants: formData.enfants !== '' ? parseInt(formData.enfants, 10) : null,
      });
      setEditMode(false);
      notifyUpdated(showNotify, entityLabel);
      dispatchHrEmployeesRefresh();
      await loadProfile();
    } catch (err) {
      notifyError(showNotify, err?.message || err?.response?.data?.error || p('save_error'));
    } finally {
      setSaving(false);
    }
  };

  const readFields = [
    { key: 'matricule', labelKey: 'field_matricule', readFromProfile: true },
    { key: 'nom', labelKey: 'field_last_name' },
    { key: 'prenom', labelKey: 'field_first_name' },
    { key: 'email', labelKey: 'field_email' },
    { key: 'fonction', labelKey: 'field_job' },
    { key: 'affectation', labelKey: 'field_department' },
  ];

  const editFields = [
    { key: 'telephone', labelKey: 'field_phone', placeholder: getBrandPhonePlaceholder() },
    { key: 'adresse', labelKey: 'field_address', full: true },
    { key: 'ville', labelKey: 'field_city', type: 'select', options: SAUDI_CITIES.map((c) => ({ value: c, label: c })) },
    { key: 'region', labelKey: 'field_region', type: 'select', options: SAUDI_REGIONS.map((r) => ({ value: r, label: r })) },
    { key: 'code_postal', labelKey: 'field_postal' },
    { key: 'situation_familiale', labelKey: 'field_family', type: 'select' },
    { key: 'enfants', labelKey: 'field_children', type: 'number' },
    { key: 'notes', labelKey: 'field_notes', type: 'textarea', full: true },
  ];

  const complianceFields = saudiProfile ? [
    isSaudi
      ? { labelKey: 'field_national_id', value: saudiProfile.nationalId || '—' }
      : { labelKey: 'field_iqama', value: saudiProfile.iqamaNumber || '—' },
    !isSaudi && saudiProfile.iqamaExpiry
      ? { labelKey: 'field_iqama_expiry', value: formatPortalDate(saudiProfile.iqamaExpiry, i18n.language), alertDays: daysUntilExpiry(saudiProfile.iqamaExpiry) }
      : null,
    { labelKey: 'field_gosi', value: saudiProfile.gosiNumber || '—' },
    !isSaudi && saudiProfile.workPermitNumber
      ? { labelKey: 'field_work_permit', value: saudiProfile.workPermitNumber }
      : null,
    !isSaudi && saudiProfile.workPermitExpiry
      ? { labelKey: 'field_work_permit_expiry', value: formatPortalDate(saudiProfile.workPermitExpiry, i18n.language), alertDays: daysUntilExpiry(saudiProfile.workPermitExpiry) }
      : null,
    saudiProfile.medicalInsuranceNumber || saudiProfile.medicalInsurance
      ? { labelKey: 'field_medical_insurance', value: saudiProfile.medicalInsuranceNumber || saudiProfile.medicalInsurance }
      : null,
    saudiProfile.medicalInsuranceExpiry
      ? { labelKey: 'field_medical_expiry', value: formatPortalDate(saudiProfile.medicalInsuranceExpiry, i18n.language), alertDays: daysUntilExpiry(saudiProfile.medicalInsuranceExpiry) }
      : null,
    { labelKey: 'field_nationality', value: isSaudi ? pc('nationality_saudi') : pc('nationality_expat') },
    saudiProfile.iban ? { labelKey: 'field_iban', value: saudiProfile.iban } : null,
    saudiProfile.dateRecrutement || profile?.dateEmbauche
      ? { labelKey: 'field_hire_date', value: formatPortalDate(saudiProfile.dateRecrutement || profile?.dateEmbauche, i18n.language) }
      : null,
    saudiProfile.passportNumber
      ? { labelKey: 'field_passport', value: saudiProfile.passportNumber }
      : null,
  ].filter(Boolean) : [];

  const employmentFields = saudiProfile ? [
    { labelKey: 'field_contract_type', value: getPortalContractLabel(saudiProfile.typeContrat, t) },
    { labelKey: 'field_probation', value: p('field_probation_days') },
    { labelKey: 'field_salary', value: formatPortalMoney(saudiProfile.salaireBase) },
    { labelKey: 'field_benefits', value: formatPortalMoney(calculatePortalBenefits(saudiProfile)) },
    { labelKey: 'field_bonus', value: formatPortalMoney(calculatePortalBonus(saudiProfile)) },
    { labelKey: 'field_eosb_balance', value: formatPortalMoney(calculatePortalEosbBalance(saudiProfile)) },
    { labelKey: 'field_eosb_accrual', value: formatPortalMoney(calculatePortalEosbAccrual(saudiProfile)) },
    { labelKey: 'field_gosi_status', value: p(getPortalGosiStatusKey(saudiProfile)) },
    { labelKey: 'field_nitaqat', value: getPortalNitaqatLabel(saudiProfile.nitaqatCategory, t) },
    { labelKey: 'years_of_service', value: p('years_of_service', { years: getPortalYearsOfService(saudiProfile).toFixed(1) }) },
  ] : [];

  const pd = (key, opts) => t(`modules.employee_portal.pages.dashboardPage.${key}`, opts);

  const laborFields = saudiProfile ? [
    { labelKey: 'field_annual_leave', value: pd('days_unit', { count: saudiProfile.soldeCongesAnnuel ?? SAUDI_ANNUAL_LEAVE_DAYS }) },
    { labelKey: 'field_sick_leave', value: pd('days_unit', { count: saudiProfile.soldeCongesMaladie ?? SAUDI_SICK_LEAVE_DAYS }) },
    { labelKey: 'field_working_hours', value: pd('labor_working_hours_value') },
    { labelKey: 'field_weekend', value: pd('labor_weekend_value') },
  ] : [];

  return (
    <div className="space-y-6 employees-container">
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-teal-500 to-teal-700">
              <UserCheck className="h-8 w-8 text-white" />
            </div>
            {p('title')}
            <button type="button" onClick={loadProfile} className="refresh-button" title={t('common.refresh')}>
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        {!editMode && profile?.id ? (
          <button
            type="button"
            data-testid="profile-edit-btn"
            onClick={() => setEditMode(true)}
            className="bg-gradient-to-r from-teal-500 to-teal-700 text-white px-6 py-3 rounded-xl font-semibold"
          >
            {p('edit_btn')}
          </button>
        ) : null}
      </div>

      {resolveError ? (
        <EmployeePortalScopeAlert error={resolveError} />
      ) : null}

      {loading ? (
        <p className="text-white/60">{t('common.loading')}</p>
      ) : profile ? (
        <form onSubmit={handleSave} className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
          {qawaem && saudiProfile ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <QawaemStatsCard label={p('stat_name')} value={`${profile.prenom || ''} ${profile.nom || ''}`.trim() || '—'} icon={UserCheck} variant="copper" />
              <QawaemStatsCard label={p('stat_salary')} value={formatPortalMoney(saudiProfile.salaireBase)} icon={Wallet} variant="forest" />
              <QawaemStatsCard label={p('stat_eosb')} value={formatPortalMoney(calculatePortalEosbBalance(saudiProfile))} icon={PiggyBank} variant="sage" />
            </div>
          ) : null}
          <div>
            <h2 className="text-white font-semibold mb-4">{p('section_identity')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {readFields.map((f) => (
                <div key={f.key}>
                  <label className="block w-full text-white/70 text-sm mb-1 font-medium text-right">{p(f.labelKey)}</label>
                  <input
                    type="text"
                    readOnly
                    value={
                      f.readFromProfile
                        ? (saudiProfile?.matricule || profile?.mat || profile?.matricule || '—')
                        : (formData[f.key] ?? '')
                    }
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white/70 font-mono"
                  />
                </div>
              ))}
            </div>
          </div>

          {complianceFields.length > 0 ? (
            <div>
              <h2 className="text-white font-semibold mb-4">{p('section_saudi_compliance')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {complianceFields.map((f) => (
                  <div key={f.labelKey}>
                    <label className="block w-full text-white/70 text-sm mb-1 font-medium text-right">{p(f.labelKey)}</label>
                    <div className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white/90">
                      {f.value}
                      {f.alertDays != null && f.alertDays >= 0 && f.alertDays <= 60 ? (
                        <span className="block text-amber-400 text-xs mt-1">{p('expiry_alert', { days: f.alertDays })}</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {employmentFields.length > 0 ? (
            <div>
              <h2 className="text-white font-semibold mb-4">{p('section_employment')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {employmentFields.map((f) => (
                  <div key={f.labelKey}>
                    <label className="block w-full text-white/70 text-sm mb-1 font-medium text-right">{p(f.labelKey)}</label>
                    <div className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white/90">{f.value}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {laborFields.length > 0 ? (
            <div>
              <h2 className="text-white font-semibold mb-4">{p('section_labor_law')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {laborFields.map((f) => (
                  <div key={f.labelKey}>
                    <label className="block w-full text-white/70 text-sm mb-1 font-medium text-right">{p(f.labelKey)}</label>
                    <div className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white/90">{f.value}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div>
            <h2 className="text-white font-semibold mb-4">{p('section_contact')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {editFields.map((f) => (
                <div key={f.key} className={f.full ? 'md:col-span-3' : ''}>
                  <label className="block w-full text-white/70 text-sm mb-1 font-medium text-right">{p(f.labelKey)}</label>
                  {f.type === 'select' ? (
                    <CustomSelect
                      value={formData[f.key] ?? ''}
                      onChange={(v) => setFormData((prev) => ({ ...prev, [f.key]: v }))}
                      options={f.options || familyOpts}
                      placeholder={p(f.key === 'region' ? 'select_region' : f.key === 'ville' ? 'select_city' : 'select_family')}
                      disabled={!editMode}
                    />
                  ) : f.type === 'textarea' ? (
                    <textarea
                      value={formData[f.key] ?? ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      readOnly={!editMode}
                      rows={3}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
                    />
                  ) : (
                    <input
                      type={f.key === 'telephone' ? 'tel' : (f.type || 'text')}
                      name={f.key}
                      data-testid={f.key === 'telephone' ? 'profile-phone-input' : undefined}
                      aria-label={p(f.labelKey)}
                      placeholder={f.placeholder || (f.key === 'telephone' ? p('phone_placeholder') : '')}
                      value={formData[f.key] ?? ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      readOnly={!editMode}
                      className={`w-full px-4 py-2 border rounded-xl text-white ${editMode ? 'bg-white/10 border-white/20' : 'bg-white/5 border-white/10 text-white/70'}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          {editMode ? (
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => { setEditMode(false); loadProfile(); }} className="px-6 py-2 border border-white/20 rounded-xl text-white">{t('common.cancel')}</button>
              <button type="submit" disabled={saving} data-testid="profile-save-btn" className="px-6 py-2 bg-gradient-to-r from-teal-500 to-teal-700 text-white rounded-xl font-semibold flex items-center gap-2">
                <Save size={18} />{t('common.save')}
              </button>
            </div>
          ) : null}
        </form>
      ) : !loading && profileResolved && !profile ? (
        <EmployeePortalUnlinkedState
          email={email}
          name={name}
          onLinked={() => {
            refreshEmployeeScope();
            void loadProfile();
          }}
        />
      ) : null}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
}
